import { action, computed, observable } from 'mobx';
import _ from 'lodash';
import {
  mergeSearchFeatureParamsWithDefaults,
  SearchFeatureParams,
} from 'types/searchFeatureParams';
import { getServerConfig } from '../services/ConfigService';
import { BASE, SSO_LOGIN_API, HOSTNAME } from '../config/constants';

class ConfigStore {
  @observable config: any = {};

  @observable didFetchConfig = false;

  @observable loginPage = 'login';

  rootStore: any;

  constructor(rootStore) {
    this.rootStore = rootStore;
  }

  getLoginPage() {
    const loginMatch = this.config?.LOGIN_METHOD?.find?.(
      (o) => o.hostname === HOSTNAME && o.method === 'redirect',
    );
    this.loginPage = loginMatch?.params?.page || 'login';
  }

  @action getServerConfig = async (force?: boolean) => {
    if (Object.keys(this.config).length && !force) {
      return this.config;
    }

    try {
      const config = await getServerConfig();

      if (config) {
        this.config = config;
        this.didFetchConfig = true;
        this.getLoginPage();
      }
    } catch (error) {
      console.error(error);
    }

    return this.config;
  };

  @computed get currentDeployment() {
    // return a value from configuration, or assume it's prod
    return this.config.OLE_APP_DEPLOYMENT || 'rht';
  }

  @computed get rezodePlatform() {
    // return a value from configuration, or assume rht platfrom
    return this.config.REZODE_PLATFORM || 'rht';
  }

  @computed get rhtDeployment(): 'devel' | 'stage' | 'rhu' | 'prod' {
    // return a value from configuration, or assume rht platfrom
    return this.config.RHT_DEPLOYMENT || 'devel';
  }

  @computed get isChina() {
    return this.rezodePlatform === 'china';
  }

  @computed get shouldSkipSentry() {
    return this.rezodePlatform === 'china';
  }

  @computed get UISettings() {
    const settings = this.config.UI_SETTINGS || {};
    const hostNameSettings =
      settings.by_hostname?.find((o) => o?.hostname === HOSTNAME) || {};

    return { ...settings, ...hostNameSettings };
  }

  @computed get showNIITFooter() {
    return this.isChina && this.UISettings.niit_footer_enabled;
  }

  @computed get ssoLoginEndpoint() {
    // return a value from configuration, or returns the default auth idp endpoint.
    const ssoLoginEndPoint =
      this.config[HOSTNAME] || this.config.OLE_SSO_LOGIN_ENDPOINT;
    return ssoLoginEndPoint ? `${BASE}${ssoLoginEndPoint}` : SSO_LOGIN_API;
  }

  // unique appid provided by Pendo to initialize Pendo SDK
  @computed get pendoAppId() {
    // return a value from configuration, or returns the default appid.
    return this.config.PENDO_APP_ID || '';
  }

  // flag to indicate whether pendo should start tracking all users or not.
  @computed get pendoTrackALLUsers() {
    return this.config.PENDO_TRACK_ALL_USERS;
  }

  // flag to indicate whether pendo should start tracking all users or not.
  @computed get pendoUsersToTrack() {
    // eslint-disable-next-line
    const _pendoUsersToTrack = this.config.PENDO_USERS || [];

    return _pendoUsersToTrack;
  }

  // URL to redirect unauthorized user
  @computed get unauthorizedRedirectUrl() {
    return this.config.UNAUTHORIZED_REDIRECT_URL || '';
  }

  @computed get arcadeSoftwareDemoUrl() {
    return this.config.ARCADE_SOFTWARE_URL || 'https://demo.arcade.software';
  }

  // Ebooks platform's URL.
  @computed get ebooksPlatformURL() {
    return this.config.EBOOKS_PLATFORM_URL;
  }

  @computed get contactOperationURL() {
    return this.config.CONTACT_OPERATION_URL || '';
  }

  @computed get learningSubscriptionURL() {
    return this.config.LEARNING_SUBSCRIPTION_URL || '';
  }

  @computed get isFreeTrialRegistrationEnabled() {
    const isEnabledLandingMat = this.loginPage === 'landing-mat';

    return Boolean(this.config.FREE_TRIAL_REGISTRATION && isEnabledLandingMat);
  }

  @computed get isFreeTrialRestartEnabled() {
    const isEnabledLandingMat = this.loginPage === 'landing-mat';
    return Boolean(this.config.FREE_TRIAL_RESTART && isEnabledLandingMat);
  }

  @computed get captchaAPIKEY() {
    return this.config.CAPTCHA_API_KEY;
  }

  @computed get pendoGuides() {
    return this.config.PENDO_GUIDES || {};
  }

  @computed get lunaSurveys() {
    return this.config.LUNA_SURVEYS || {};
  }

  @computed get staticBaseURL() {
    return _.get(
      this.config,
      'STATIC_BASE_URL',
      'https://static.ole.redhat.com/rhls/',
    );
  }

  @computed get footerURLs() {
    return this.config.FOOTER_URLS || {};
  }

  @computed get bbbCompatibilityURL() {
    return this.config.BBB_COMPATIBILITY_URL;
  }

  @computed get searchFeatureParams(): SearchFeatureParams {
    return mergeSearchFeatureParamsWithDefaults(
      this.config.SEARCH_FEATURE_PARAMS,
    );
  }

  @computed get networkTestHosts(): Record<string, string> {
    const defaults: Record<string, string> = {
      apac: 'rhtapps-apac150.ole.redhat.com',
      emea: 'rhtapps-emea160.ole.redhat.com/',
      latam: 'rhtapps-na150.ole.redhat.com',
      na: 'rhtapps-na150.ole.redhat.com',
      northamerica: 'rhtapps-na150.ole.redhat.com',
    };

    return this.config.NETWORK_TEST_HOSTS || defaults;
  }
}

export default ConfigStore;
